import React from 'react';
import { useTheme } from '@emotion/react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { Primary, Secondary } from 'ui-components/Buttons';

/**
 * Docks at the bottom once something has changed, and says what will happen.
 *
 * Nothing on this screen takes effect as you type — it all goes live together
 * when you publish — so the page needs one place that says so, rather than a
 * Save button sitting there permanently whether or not there is anything to save.
 */
const SaveBar = ({ dirty, saving, summary, problems = [], onSave, onDiscard, target = 'publishes to the pricing pages', saveLabel = 'Save & publish' }) => {
    const theme = useTheme();
    if (!dirty && problems.length === 0) return null;

    const hasProblems = problems.length > 0;

    return (
        <Box sx={{
            // Pinned to the foot of the viewport rather than sitting after the
            // editor: the panel above reserves a fixed height, and with a long
            // plan the bar landed below the fold — the one thing on this screen
            // that must never be out of reach.
            position: 'sticky',
            bottom: '0.75rem',
            zIndex: 10,
            marginTop: '0.25rem',
            padding: '0.875rem 1.25rem',
            borderRadius: '12px',
            backgroundColor: theme.palette.customColors.whiteBG,
            border: `1px solid ${hasProblems ? theme.palette.customColors.r75 : theme.palette.customColors.b50}`,
            boxShadow: '0 -6px 24px rgba(16, 24, 40, 0.08)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '1rem',
            flexWrap: 'wrap',
        }}>
            <Box>
                <Typography sx={{
                    fontWeight: 700,
                    color: hasProblems ? theme.palette.customColors.r500 : theme.palette.customColors.g300,
                }}>
                    {hasProblems
                        ? `${problems.length} problem${problems.length === 1 ? '' : 's'} to fix`
                        : 'Unsaved changes'}
                </Typography>
                <Typography sx={{ fontSize: '0.8125rem', color: theme.palette.customColors.g200 }}>
                    {hasProblems
                        ? problems[0].message
                        : `${summary} · ${target}`}
                </Typography>
            </Box>

            <Box display="flex" alignItems="center" gap={1.5}>
                <Secondary onClick={onDiscard} disabled={saving} sx={{ padding: '0.5rem 1.25rem' }}>
                    Discard
                </Secondary>
                <Primary onClick={onSave} disabled={saving} sx={{ padding: '0.625rem 1.5rem', minWidth: '10rem' }}>
                    {saving ? <CircularProgress sx={{ color: 'white' }} size={'1.2rem'} /> : saveLabel}
                </Primary>
            </Box>
        </Box>
    );
};

export default SaveBar;
